import React from 'react';

import { createStatusChipComponent, type StatusChipProps, type StatusConfig } from './CliveStatusUtils';

// Clive Store Processing Status Enum (base statuses)
export enum CliveStoreProcessingStatus {
  PROCESSING = 'processing',
  ERROR = 'error',
  ERROR_UPLOADING = 'error_uploading',
  SUCCESS = 'success',
  ANALYZING = 'analyzing',
  DEFAULTS_APPLIED = 'defaults_applied',
  ERROR_CLASSIFYING_DOCUMENT_TYPE = 'error_classifying_document_type',
  ERROR_UNSUPPORTED_FILE_TYPE = 'error_unsupported_file_type',
  ERROR_FILE_TOO_LARGE = 'error_file_too_large',
}

// Base status configuration (shared by all contexts)
export const CLIVE_STORE_STATUS_CONFIG: StatusConfig<CliveStoreProcessingStatus> = {
  [CliveStoreProcessingStatus.PROCESSING]: { iconType: 'loading' as const, content: 'Processing' },
  [CliveStoreProcessingStatus.SUCCESS]: { iconType: 'happy' as const, content: 'Completed' },
  [CliveStoreProcessingStatus.ANALYZING]: { iconType: 'loading' as const, content: 'Analyzing' },
  [CliveStoreProcessingStatus.DEFAULTS_APPLIED]: { iconType: 'happy' as const, content: 'Completed' },
  [CliveStoreProcessingStatus.ERROR]: { iconType: 'error' as const, content: 'Failed' },
  [CliveStoreProcessingStatus.ERROR_UPLOADING]: { iconType: 'error' as const, content: 'Failed' },
  [CliveStoreProcessingStatus.ERROR_CLASSIFYING_DOCUMENT_TYPE]: { iconType: 'error' as const, content: 'Failed' },
  [CliveStoreProcessingStatus.ERROR_UNSUPPORTED_FILE_TYPE]: { iconType: 'error' as const, content: 'Failed' },
  [CliveStoreProcessingStatus.ERROR_FILE_TOO_LARGE]: { iconType: 'error' as const, content: 'Failed' },
} as const;

// Status configuration for UPLOADING LIST (no document created yet)
// ALL errors show "Failed" for consistency
const UPLOADING_STATUS_CONFIG: StatusConfig<CliveStoreProcessingStatus> = {
  ...CLIVE_STORE_STATUS_CONFIG,
  [CliveStoreProcessingStatus.ERROR]: { iconType: 'error' as const, content: 'Failed' },
  [CliveStoreProcessingStatus.ERROR_UNSUPPORTED_FILE_TYPE]: {
    iconType: 'error' as const,
    content: 'Failed',
  },
  [CliveStoreProcessingStatus.ERROR_FILE_TOO_LARGE]: {
    iconType: 'error' as const,
    content: 'Failed',
  },
} as const;

// Create chip components for different contexts
const UploadingStatusChip = createStatusChipComponent(
  UPLOADING_STATUS_CONFIG,
  Object.values(CliveStoreProcessingStatus)
);
const DocumentsStatusChip = createStatusChipComponent(
  CLIVE_STORE_STATUS_CONFIG,
  Object.values(CliveStoreProcessingStatus)
);

// Context-aware wrapper component
interface CliveStoreStatusChipProps extends StatusChipProps<CliveStoreProcessingStatus> {
  hasDocument?: boolean; // true if document exists (created), false if still uploading
}

export const CliveStoreStatusChip: React.FC<CliveStoreStatusChipProps> = ({ status, variant, hasDocument }) => {
  const ChipComponent = hasDocument ? DocumentsStatusChip : UploadingStatusChip;
  return <ChipComponent status={status} variant={variant} />;
};
CliveStoreStatusChip.displayName = 'CliveStoreStatusChip';
